import axiosInstance from "@/axios";

export const state = {
    customersData: [], // Changed from empty object to empty array
    customersLoading: false,
    customersMsg: null,
    errorMsg: null,
    successMsg: null,
    pagination: {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10,
        hasNext: false,
        hasPrev: false
    }
}

export const mutations = {
    SET_CUSTOMERS_DATA(state, customersData) {
        state.customersData = customersData
    },
    SET_LOADING(state, customersLoading) {
        state.customersLoading = customersLoading
    },
    SET_CUSTOMERS_MSG(state, customersMsg) {
        state.customersMsg = customersMsg
    },
    SET_PAGINATION(state, pagination) {
        // Ensure proper reactivity by creating a new object reference
        state.pagination = { ...pagination }
    },
    UPDATE_CUSTOMER(state, updateCustomer) {
        // Update the customer in customersData if it exists
        if (Array.isArray(state.customersData)) {
            const index = state.customersData.findIndex(customer => customer.id === updateCustomer.id);
            if (index !== -1) {
                state.customersData[index] = { ...state.customersData[index], ...updateCustomer };
            }
        }
    },
    DELETE_CUSTOMER(state, customerId) {
        if (Array.isArray(state.customersData)) {
            state.customersData = state.customersData.filter(customer => customer.id !== customerId);
        }
    },
    SET_ERROR_MSG(state, errorMsg) {
        state.errorMsg = errorMsg
    },
    SET_SUCCESS_MSG(state, successMsg) {
        state.successMsg = successMsg
    },
}

export const getters = {
    customersData: state => state.customersData,
    customersLoading: state => state.customersLoading,
    customersMsg: state => state.customersMsg,
    customersPagination: state => state.pagination,
    errorMsg: state => state.errorMsg,
    successMsg: state => state.successMsg,
}

export const actions = {
    async getCustomersData({ commit }, params = {}) {
        const { page = 1, perPage = 10, ...filterParams } = params;

        commit('SET_LOADING', true)

        try {
            const response = await axiosInstance({ requiresAuth: true }).get(`/agent/customers`, {
                params: { page, perPage, ...filterParams }
            });

            // Use the full response object, not just response.data
            let responseData = response.data;

            // Check if the response has pagination structure at the top level
            if (response.data && response.links && response.meta) {
                // Restructure to match expected format
                responseData = {
                    data: response.data,
                    links: response.links,
                    meta: response.meta
                };
            }

            let customersData = [];
            let paginationData = {
                currentPage: parseInt(page) || 1,
                totalPages: 1,
                totalItems: 0,
                itemsPerPage: parseInt(perPage) || 10,
                hasNext: false,
                hasPrev: false
            };

            // Enhanced pagination extraction with proper meta and links handling

            // TEMPORARY FIX: If we get a direct array, try different API parameters
            if (Array.isArray(responseData) && responseData.length === parseInt(perPage)) {
                try {
                    // Try alternative parameter names
                    const alternativeResponse = await axiosInstance({ requiresAuth: true }).get(`/agent/customers`, {
                        params: { page: page, per_page: perPage, ...filterParams }  // Try snake_case
                    });

                    if (alternativeResponse.data && !Array.isArray(alternativeResponse.data)) {
                        // Use the alternative response instead
                        responseData = alternativeResponse.data;
                    }
                } catch (error) {
                    // Alternative API call failed, using original response
                }
            }

            if (responseData && responseData.data && Array.isArray(responseData.data)) {
                // Standard Laravel/API paginated response: { data: [...], meta: {...}, links: {...} }
                customersData = responseData.data;

                const meta = responseData.meta;
                const links = responseData.links;

                if (meta) {
                    // Use meta object for pagination info (Laravel style)
                    paginationData = {
                        currentPage: parseInt(meta.current_page) || parseInt(page) || 1,
                        totalPages: parseInt(meta.last_page) || 1,
                        totalItems: parseInt(meta.total) || customersData.length,
                        itemsPerPage: parseInt(meta.per_page) || parseInt(perPage) || 10,
                        hasNext: !!(links?.next || (meta.current_page && meta.last_page && meta.current_page < meta.last_page)),
                        hasPrev: !!(links?.prev || (meta.current_page && meta.current_page > 1))
                    };
                } else {
                    // Fallback to response-level pagination data
                    paginationData = {
                        currentPage: parseInt(responseData.current_page) || parseInt(page) || 1,
                        totalPages: parseInt(responseData.last_page) || Math.ceil((parseInt(responseData.total) || customersData.length) / parseInt(perPage)) || 1,
                        totalItems: parseInt(responseData.total) || customersData.length,
                        itemsPerPage: parseInt(responseData.per_page) || parseInt(perPage) || 10,
                        hasNext: !!(responseData.next_page_url || (responseData.current_page && responseData.last_page && responseData.current_page < responseData.last_page)),
                        hasPrev: !!(responseData.prev_page_url || (responseData.current_page && responseData.current_page > 1))
                    };
                }
            } else if (responseData && responseData.results && Array.isArray(responseData.results)) {
                // Django REST Framework style: { results: [...], count: N, next: URL, previous: URL }
                customersData = responseData.results;

                paginationData = {
                    currentPage: parseInt(page) || 1,
                    totalPages: Math.ceil((parseInt(responseData.count) || customersData.length) / parseInt(perPage)) || 1,
                    totalItems: parseInt(responseData.count) || customersData.length,
                    itemsPerPage: parseInt(perPage) || 10,
                    hasNext: !!responseData.next,
                    hasPrev: !!responseData.previous
                };
            } else if (Array.isArray(responseData)) {
                // Direct array response (no pagination metadata) - treat as single page
                customersData = responseData;
                paginationData = {
                    currentPage: 1,
                    totalPages: 1,
                    totalItems: customersData.length,
                    itemsPerPage: customersData.length || parseInt(perPage) || 10,
                    hasNext: false,
                    hasPrev: false
                };
            } else {
                // Final fallback
                customersData = responseData?.data || responseData || [];
                const totalItems = customersData.length || 0;
                paginationData = {
                    currentPage: 1,
                    totalPages: Math.ceil(totalItems / parseInt(perPage)) || 1,
                    totalItems: totalItems,
                    itemsPerPage: parseInt(perPage) || 10,
                    hasNext: false,
                    hasPrev: false
                };
            }

            commit('SET_CUSTOMERS_DATA', customersData);
            commit('SET_PAGINATION', paginationData);
            commit('SET_CUSTOMERS_MSG', null);
        } catch (error) {
            commit('SET_CUSTOMERS_MSG', error.response?.data?.message || 'Failed to fetch customers');
            commit('SET_CUSTOMERS_DATA', []);
        } finally {
            commit('SET_LOADING', false);
        }
    },
    async getSimplifiedCustomersData({ commit }) {
        commit('SET_LOADING', true)
        try {
            const response = await axiosInstance({ requiresAuth: true }).get(`/agent/simplified/customers`);

            const responseData = response.data;

            // For simplified endpoint, expect direct array or simple structure
            let customersData;
            if (Array.isArray(responseData)) {
                customersData = responseData;
            } else if (responseData && Array.isArray(responseData.data)) {
                customersData = responseData.data;
            } else {
                customersData = responseData || [];
            }

            commit('SET_CUSTOMERS_DATA', customersData);
            commit('SET_CUSTOMERS_MSG', null);
        } catch (error) {
            commit('SET_CUSTOMERS_MSG', error.response?.data?.message || 'Failed to fetch customers');
        } finally {
            commit('SET_LOADING', false);
        }
    },
    async updateCustomer({ commit }, updateCustomer) {
        // Clear previous messages
        commit('SET_ERROR_MSG', null);
        commit('SET_SUCCESS_MSG', null);

        try {
            // Using proper async/await pattern
            const response = await axiosInstance({ requiresAuth: true })
                .patch(`/agent/customers/${updateCustomer.id}`, updateCustomer);

            // If we get here, the request was successful
            commit('UPDATE_CUSTOMER', updateCustomer);
            commit('SET_SUCCESS_MSG', 'Customer updated successfully');

            return Promise.resolve(response); // Return successful response
        } catch (error) {
            // Properly handle the error
            const errorMessage = error.response?.data?.message || 'An error occurred while updating the customer';
            commit('SET_ERROR_MSG', errorMessage);

            // Re-throw the error so the component can also handle it if needed
            return Promise.reject(error);
        }
    },
    async deleteCustomer({ commit }, customerId) {
        commit('SET_LOADING', true);
        try {
            const response = await axiosInstance({ requiresAuth: true })
                .delete(`/agent/customers/${customerId}`);

            // Remove the customer from the local state
            commit('DELETE_CUSTOMER', customerId);
            commit('SET_CUSTOMERS_MSG', 'Customer deleted successfully');

            return Promise.resolve(response);
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'An error occurred while deleting the customer';
            commit('SET_CUSTOMERS_MSG', errorMessage);
            return Promise.reject(error);
        } finally {
            commit('SET_LOADING', false);
        }
    },
}